import { useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clampDuration, snapToGrid } from '../../../lib/timeline';
import { useToast } from '../../../components/Toast/ToastProvider';
import { api } from '../../../api/client';
import type { TimelineBlock } from '../../../types';

interface ResizeHandleProps {
  block: TimelineBlock;
  currentDuration: number;
  pxPerHour: number;
  onResizing?: (duration: number | null) => void;
}

export function ResizeHandle({ block, currentDuration, pxPerHour, onResizing }: ResizeHandleProps) {
  const startYRef = useRef(0);
  const startDurationRef = useRef(0);
  const latestDurationRef = useRef(currentDuration);

  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const resizeMutation = useMutation({
    mutationFn: ({ scheduledDuration }: { scheduledDuration: number }) =>
      api.timelineBlocks.update(block.id, { scheduledDuration }),
    onMutate: async ({ scheduledDuration }) => {
      await queryClient.cancelQueries({ queryKey: ['timeline-blocks', block.date] });
      const previous = queryClient.getQueryData<TimelineBlock[]>(['timeline-blocks', block.date]);
      queryClient.setQueryData<TimelineBlock[]>(['timeline-blocks', block.date], (old) =>
        old?.map((b) => b.id === block.id ? { ...b, scheduledDuration } : b)
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['timeline-blocks', block.date], context.previous);
      }
      addToast("Couldn't resize — duration restored", 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['timeline-blocks', block.date] });
      queryClient.invalidateQueries({ queryKey: ['timeline-blocks', 'upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['timeline-blocks', 'range'] });
    },
  });

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    startYRef.current = e.clientY;
    startDurationRef.current = currentDuration;
    latestDurationRef.current = currentDuration;

    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);

    const onMove = (moveEvt: PointerEvent) => {
      const deltaY = moveEvt.clientY - startYRef.current;
      const deltaDuration = (deltaY * 60) / pxPerHour;
      const newDuration = clampDuration(snapToGrid(startDurationRef.current + deltaDuration));
      latestDurationRef.current = newDuration;
      onResizing?.(newDuration);
    };

    const onCancel = () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      onResizing?.(null);
    };

    const onUp = () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      onResizing?.(null);

      const finalDuration = latestDurationRef.current;
      if (finalDuration !== currentDuration) {
        resizeMutation.mutate({ scheduledDuration: finalDuration });
      }
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onCancel);
  }, [block.id, block.date, currentDuration, onResizing, resizeMutation]);

  return (
    <div
      onPointerDown={handlePointerDown}
      className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize group/resize z-20"
      style={{ touchAction: 'none' }}
    >
      <div className="absolute bottom-0.5 left-1/4 right-1/4 h-0.5 rounded-full bg-text-muted/0 group-hover/resize:bg-text-muted/40 transition-colors" />
    </div>
  );
}
